import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import { Controller, Path, useForm } from "react-hook-form";
import {
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";
import NurseDateField from "../NurseDateField";
import { normalizeDateInputValue } from "../dateValueUtils";
import { useNurseFormAutoSave } from "../useNurseFormAutoSave";

export interface Step09FormValues {
    // Vision
    visionDateOfExam: string;
    visionLevel: "noProblem" | "largeprint" | "limitedVision" | "significantImpaired" | "severelyImpaired" | "blind" | "";
    visionGlaucomaL: boolean; visionGlaucomaR: boolean;
    visionCataractsL: boolean; visionCataractsR: boolean; visionCataractsRemoved: boolean;
    visionMacularL: boolean; visionMacularR: boolean;
    visionGlasses: boolean; visionContactLenses: boolean;
    visionSurgeries: string; visionOther: string;
    visionCaregiverCleanGlasses: boolean; visionCaregiverEyeMeds: boolean;
    visionComments: string;
    // Hearing
    hearingDateOfExam: string;
    hearingLevel: "noProblem" | "quietSetting" | "specialSituations" | "highlyImpaired" | "";
    hearingLossL: boolean; hearingLossR: boolean;
    hearingAidsL: boolean; hearingAidsR: boolean;
    hearingCaregiverBatteries: boolean;
    hearingComments: string;
    // Communication: Making Self Understood
    commMakingLevel: "noProblem" | "someDifficulty" | "simpleRequests" | "rarelyAble" | "";
    commSpeechClarity: boolean; commSignLanguage: boolean; commReadsLips: boolean;
    commDevice: string;
    commCaregiverListenCarefully: boolean; commCaregiverAllowTime: boolean;
    commMakingComments: string;
    // Communication: Understanding Others
    commUnderstandLevel: "noProblem" | "usuallyAble" | "sometimesAble" | "rarelyAble" | "";
    commUnderstandSpeakSlowly: boolean; commUnderstandRepeat: boolean; commUnderstandEyeContact: boolean;
    commUnderstandComments: string;
    // Dental
    dentalDateOfExam: string;
    dentalNoProblem: boolean; dentalOwnTeeth: boolean; dentalMissingTeeth: boolean;
    dentalDenturesUpper: boolean; dentalDenturesLower: boolean;
    dentalPartialsUpper: boolean; dentalPartialsLower: boolean;
    dentalBrokenLoose: boolean; dentalInflamedGums: boolean; dentalDryMouth: boolean;
    dentalCaregiverInsertRemove: boolean; dentalCaregiverClean: boolean;
    dentalComments: string;
}

export const DEFAULT_STEP09: Step09FormValues = {
    visionDateOfExam: "", visionLevel: "",
    visionGlaucomaL: false, visionGlaucomaR: false, visionCataractsL: false, visionCataractsR: false,
    visionCataractsRemoved: false, visionMacularL: false, visionMacularR: false,
    visionGlasses: false, visionContactLenses: false, visionSurgeries: "", visionOther: "",
    visionCaregiverCleanGlasses: false, visionCaregiverEyeMeds: false, visionComments: "",
    hearingDateOfExam: "", hearingLevel: "",
    hearingLossL: false, hearingLossR: false, hearingAidsL: false, hearingAidsR: false,
    hearingCaregiverBatteries: false, hearingComments: "",
    commMakingLevel: "", commSpeechClarity: false, commSignLanguage: false, commReadsLips: false,
    commDevice: "", commCaregiverListenCarefully: false, commCaregiverAllowTime: false, commMakingComments: "",
    commUnderstandLevel: "", commUnderstandSpeakSlowly: false, commUnderstandRepeat: false,
    commUnderstandEyeContact: false, commUnderstandComments: "",
    dentalDateOfExam: "", dentalNoProblem: false, dentalOwnTeeth: false, dentalMissingTeeth: false,
    dentalDenturesUpper: false, dentalDenturesLower: false, dentalPartialsUpper: false, dentalPartialsLower: false,
    dentalBrokenLoose: false, dentalInflamedGums: false, dentalDryMouth: false,
    dentalCaregiverInsertRemove: false, dentalCaregiverClean: false, dentalComments: "",
};

export interface Step09Props {
    sectionData: Partial<Step09FormValues> | null;
    residentId: string;
    formId: string;
    onSave: (data: Step09FormValues) => Promise<void>;
    onNext: () => void;
    onBack: () => void;
    loading?: boolean;
}

const VISION_LEVELS: Array<{ value: Step09FormValues["visionLevel"]; label: string }> = [
    { value: "noProblem", label: "No problem identified" },
    { value: "largeprint", label: "Impaired — sees large print" },
    { value: "limitedVision", label: "Limited vision — can see shapes, headlines and identify objects" },
    { value: "significantImpaired", label: "Significantly impaired — difficulty identifying objects" },
    { value: "severelyImpaired", label: "Severely impaired — sees only light/colors, cannot track objects" },
    { value: "blind", label: "Blind" },
];

const HEARING_LEVELS: Array<{ value: Step09FormValues["hearingLevel"]; label: string }> = [
    { value: "noProblem", label: "No problem identified" },
    { value: "quietSetting", label: "Difficulty when not in quiet setting" },
    { value: "specialSituations", label: "Hears only in special situations — must adjust tonal quality and volume" },
    { value: "highlyImpaired", label: "Highly impaired — no useful hearing" },
];

const COMM_MAKING_LEVELS: Array<{ value: Step09FormValues["commMakingLevel"]; label: string }> = [
    { value: "noProblem", label: "No problem identified" },
    { value: "someDifficulty", label: "Usually able — has some difficulty finding words or finishing thoughts" },
    { value: "simpleRequests", label: "Sometimes able — can make simple requests regarding needs and preferences" },
    { value: "rarelyAble", label: "Rarely/never able — someone else must interpret sounds or body language" },
];

const COMM_UNDERSTAND_LEVELS: Array<{ value: Step09FormValues["commUnderstandLevel"]; label: string }> = [
    { value: "noProblem", label: "No problem identified" },
    { value: "usuallyAble", label: "Usually able — demonstrates understanding but may miss some part or intent" },
    { value: "sometimesAble", label: "Sometimes able — frequent difficulty; responds to simple/direct questions" },
    { value: "rarelyAble", label: "Rarely/never able — very limited ability" },
];

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div className="col-12 md:col-4 mb-2">
        <div className="text-xs font-medium mb-1" style={{ color: "#6b7280" }}>{label}</div>
        <div className="text-sm font-medium" style={{ color: value ? "#111827" : "#9ca3af" }}>{value || "—"}</div>
    </div>
);

const formatSelected = (items: Array<string | false | null | undefined>) => {
    const selected = items.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    return selected.length ? selected.join(", ") : undefined;
};

const Step09SensoryCommunication: React.FC<Step09Props> = ({
    sectionData, formId, onSave, onNext, onBack, loading,
}) => {
    const toast = useRef<Toast>(null);
    const [enableEditing, setEnableEditing] = useState<boolean>(false);
    const defaultValues: Step09FormValues = {
        ...DEFAULT_STEP09,
        ...(sectionData ?? {}),
        visionDateOfExam: normalizeDateInputValue(sectionData?.visionDateOfExam),
        hearingDateOfExam: normalizeDateInputValue(sectionData?.hearingDateOfExam),
        dentalDateOfExam: normalizeDateInputValue(sectionData?.dentalDateOfExam),
    };
    const { control, handleSubmit, watch, reset } = useForm<Step09FormValues>({ defaultValues });

    const onSubmit = async (data: Step09FormValues) => { await onSave(data); };
    const handleSave = async (data: Step09FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Sensory & communication saved.", life: 3000 });
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    const handleSaveAndContinue = async (data: Step09FormValues) => {
        try {
            await onSave(data);
            toast.current?.show({ severity: "success", summary: "Saved", detail: "Saved. Moving to next step.", life: 2000 });
            onNext();
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: "Save failed", detail: error?.message, life: 4000 });
        }
    };
    useNurseFormAutoSave({ enabled: enableEditing && !!formId, baselineData: defaultValues, watch, handleSubmit, onSubmit });

    const values = watch();

    const cb = (name: Path<Step09FormValues>, label: string) => (
        <Controller key={name} name={name} control={control} render={({ field: f }) => (
            <div className="flex align-items-center gap-2 mb-1">
                <Checkbox inputId={name} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                <label htmlFor={name} className="text-sm cursor-pointer">{label}</label>
            </div>
        )} />
    );

    const lrPair = (left: Path<Step09FormValues>, right: Path<Step09FormValues>, label: string) => (
        <div key={label} className="flex align-items-center gap-3 mb-1">
            <span className="text-sm" style={{ minWidth: 120 }}>{label}</span>
            <Controller name={left} control={control} render={({ field: f }) => (
                <div className="flex align-items-center gap-1">
                    <Checkbox inputId={left} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                    <label htmlFor={left} className="text-xs">L</label>
                </div>
            )} />
            <Controller name={right} control={control} render={({ field: f }) => (
                <div className="flex align-items-center gap-1">
                    <Checkbox inputId={right} checked={!!f.value} onChange={e => f.onChange(e.checked)} />
                    <label htmlFor={right} className="text-xs">R</label>
                </div>
            )} />
        </div>
    );

    const radioLevel = <K extends string>(fieldName: Path<Step09FormValues>, options: Array<{ value: K; label: string }>) => (
        <Controller name={fieldName} control={control} render={({ field: f }) => (
            <div className="flex flex-column gap-1">
                {options.map(opt => (
                    <div key={opt.value as string} className="flex align-items-start gap-2">
                        <RadioButton inputId={`${fieldName}_${opt.value}`} value={opt.value} checked={f.value === opt.value} onChange={() => f.onChange(opt.value)} style={{ marginTop: 2 }} />
                        <label htmlFor={`${fieldName}_${opt.value}`} className="text-sm cursor-pointer">{opt.label}</label>
                    </div>
                ))}
            </div>
        )} />
    );

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={handleSubmit(handleSave)}>
                <div className="flex justify-content-center gap-2 mb-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
                {!enableEditing ? (
                    <>
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Sensory & Communication", "Vision, hearing, communication, and dental summary.", "pi pi-eye")}
                            <div className="p-3 grid">
                                <InfoRow label="Vision Level" value={VISION_LEVELS.find(opt => opt.value === values.visionLevel)?.label} />
                                <InfoRow label="Vision Last Exam" value={values.visionDateOfExam} />
                                <InfoRow label="Vision Conditions" value={formatSelected([
                                    values.visionGlaucomaL && "Glaucoma (L)",
                                    values.visionGlaucomaR && "Glaucoma (R)",
                                    values.visionCataractsL && "Cataracts (L)",
                                    values.visionCataractsR && "Cataracts (R)",
                                    values.visionCataractsRemoved && "Cataracts removed",
                                    values.visionMacularL && "Macular issue (L)",
                                    values.visionMacularR && "Macular issue (R)",
                                ])} />
                                <InfoRow label="Vision Devices / Supports" value={formatSelected([
                                    values.visionGlasses && "Glasses",
                                    values.visionContactLenses && "Contact lenses",
                                    values.visionSurgeries && `Surgeries: ${values.visionSurgeries}`,
                                    values.visionOther && `Other: ${values.visionOther}`,
                                    values.visionCaregiverCleanGlasses && "Caregiver cleans glasses",
                                    values.visionCaregiverEyeMeds && "Caregiver gives eye meds",
                                ])} />
                                <InfoRow label="Vision Comments" value={values.visionComments} />

                                <InfoRow label="Hearing Level" value={HEARING_LEVELS.find(opt => opt.value === values.hearingLevel)?.label} />
                                <InfoRow label="Hearing Last Exam" value={values.hearingDateOfExam} />
                                <InfoRow label="Hearing Findings" value={formatSelected([
                                    values.hearingLossL && "Hearing loss (L)",
                                    values.hearingLossR && "Hearing loss (R)",
                                    values.hearingAidsL && "Hearing aid (L)",
                                    values.hearingAidsR && "Hearing aid (R)",
                                    values.hearingCaregiverBatteries && "Caregiver helps with batteries/aids",
                                ])} />
                                <InfoRow label="Hearing Comments" value={values.hearingComments} />

                                <InfoRow label="Making Self Understood" value={COMM_MAKING_LEVELS.find(opt => opt.value === values.commMakingLevel)?.label} />
                                <InfoRow label="Making Self Understood Details" value={formatSelected([
                                    values.commSpeechClarity && "Problems with speech clarity",
                                    values.commSignLanguage && "Uses sign language",
                                    values.commReadsLips && "Reads lips",
                                    values.commDevice && `Device: ${values.commDevice}`,
                                    values.commCaregiverListenCarefully && "Caregiver listens carefully",
                                    values.commCaregiverAllowTime && "Caregiver allows time to finish thoughts",
                                ])} />
                                <InfoRow label="Making Self Understood Comments" value={values.commMakingComments} />

                                <InfoRow label="Understanding Others" value={COMM_UNDERSTAND_LEVELS.find(opt => opt.value === values.commUnderstandLevel)?.label} />
                                <InfoRow label="Understanding Others Support" value={formatSelected([
                                    values.commUnderstandSpeakSlowly && "Speak slowly and clearly",
                                    values.commUnderstandRepeat && "Repeat as needed",
                                    values.commUnderstandEyeContact && "Use good eye contact",
                                ])} />
                                <InfoRow label="Understanding Others Comments" value={values.commUnderstandComments} />

                                <InfoRow label="Dental Last Exam" value={values.dentalDateOfExam} />
                                <InfoRow label="Dental Findings" value={formatSelected([
                                    values.dentalNoProblem && "No problem identified",
                                    values.dentalOwnTeeth && "Own teeth",
                                    values.dentalMissingTeeth && "Missing teeth",
                                    values.dentalDenturesUpper && "Dentures — Upper",
                                    values.dentalDenturesLower && "Dentures — Lower",
                                    values.dentalPartialsUpper && "Partials — Upper",
                                    values.dentalPartialsLower && "Partials — Lower",
                                    values.dentalBrokenLoose && "Broken/loose teeth",
                                    values.dentalInflamedGums && "Inflamed/bleeding gums",
                                    values.dentalDryMouth && "Dry mouth",
                                ])} />
                                <InfoRow label="Dental Caregiver Support" value={formatSelected([
                                    values.dentalCaregiverInsertRemove && "Help with inserting/removing dentures",
                                    values.dentalCaregiverClean && "Help with cleaning dentures",
                                ])} />
                                <InfoRow label="Dental Comments" value={values.dentalComments} />
                            </div>
                        </Card>
                    </>
                ) : (
                    <>
                        {/* Vision */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Vision", "Visual acuity, conditions, and assistive devices.", "pi pi-eye")}
                            <div className="p-3">
                                <div className="mb-3">
                                    <label className="font-medium text-sm block mb-1">Date of last exam</label>
                                    <Controller name="visionDateOfExam" control={control} render={({ field: f }) => (
                                        <NurseDateField value={f.value} onChange={f.onChange} placeholder="Date of last vision exam" style={{ width: 220 }} />
                                    )} />
                                </div>
                                <p className="font-semibold text-sm mb-2">Vision Level</p>
                                <div className="mb-3">{radioLevel("visionLevel", VISION_LEVELS)}</div>
                                <p className="font-semibold text-sm mb-2">Conditions</p>
                                {lrPair("visionGlaucomaL", "visionGlaucomaR", "Glaucoma")}
                                {lrPair("visionCataractsL", "visionCataractsR", "Cataracts")}
                                {cb("visionCataractsRemoved", "Cataracts removed")}
                                {lrPair("visionMacularL", "visionMacularR", "Macular")}
                                <p className="font-semibold text-sm mt-2 mb-2">Assistive Devices</p>
                                {cb("visionGlasses", "Glasses")}
                                {cb("visionContactLenses", "Contact lenses")}
                                <div className="mt-2 mb-2">
                                    <label className="font-medium text-sm block mb-1">Surgeries</label>
                                    <Controller name="visionSurgeries" control={control} render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Eye surgeries" />} />
                                </div>
                                <div className="mb-2">
                                    <label className="font-medium text-sm block mb-1">Other</label>
                                    <Controller name="visionOther" control={control} render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Other vision notes" />} />
                                </div>
                                <p className="font-semibold text-sm mb-2">Caregiver Will</p>
                                {cb("visionCaregiverCleanGlasses", "Clean glasses as needed")}
                                {cb("visionCaregiverEyeMeds", "Administer eye medications as needed")}
                                <div className="mt-2">
                                    <label className="font-medium text-sm block mb-1">Comments</label>
                                    <Controller name="visionComments" control={control} render={({ field: f }) => <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Vision comments" />} />
                                </div>
                            </div>
                        </Card>

                        {/* Hearing */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Hearing", "Hearing ability, loss, and aids.", "pi pi-volume-up")}
                            <div className="p-3">
                                <div className="mb-3">
                                    <label className="font-medium text-sm block mb-1">Date of last exam</label>
                                    <Controller name="hearingDateOfExam" control={control} render={({ field: f }) => (
                                        <NurseDateField value={f.value} onChange={f.onChange} placeholder="Date of last hearing exam" style={{ width: 220 }} />
                                    )} />
                                </div>
                                <p className="font-semibold text-sm mb-2">Hearing Level</p>
                                <div className="mb-3">{radioLevel("hearingLevel", HEARING_LEVELS)}</div>
                                {lrPair("hearingLossL", "hearingLossR", "Hearing Loss")}
                                {lrPair("hearingAidsL", "hearingAidsR", "Hearing Aids")}
                                {cb("hearingCaregiverBatteries", "Caregiver will help with batteries/aids")}
                                <div className="mt-2">
                                    <label className="font-medium text-sm block mb-1">Comments</label>
                                    <Controller name="hearingComments" control={control} render={({ field: f }) => <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Hearing comments" />} />
                                </div>
                            </div>
                        </Card>

                        {/* Communication: Making Self Understood */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Communication: Making Self Understood", "Ability to express needs and preferences.", "pi pi-comments")}
                            <div className="p-3">
                                <p className="font-semibold text-sm mb-2">Level</p>
                                <div className="mb-3">{radioLevel("commMakingLevel", COMM_MAKING_LEVELS)}</div>
                                {cb("commSpeechClarity", "Problems with speech clarity")}
                                {cb("commSignLanguage", "Uses sign language")}
                                {cb("commReadsLips", "Reads lips")}
                                <div className="mt-2 mb-2">
                                    <label className="font-medium text-sm block mb-1">Communication device</label>
                                    <Controller name="commDevice" control={control} render={({ field: f }) => <InputText {...f} className="w-full" placeholder="Describe device" />} />
                                </div>
                                <p className="font-semibold text-sm mb-2">Caregiver Will</p>
                                {cb("commCaregiverListenCarefully", "Listen carefully when communicating needs and preferences")}
                                {cb("commCaregiverAllowTime", "Allow time to finish thoughts and find words")}
                                <div className="mt-2">
                                    <label className="font-medium text-sm block mb-1">Comments</label>
                                    <Controller name="commMakingComments" control={control} render={({ field: f }) => <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Comments" />} />
                                </div>
                            </div>
                        </Card>

                        {/* Communication: Understanding Others */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Communication: Ability to Understand Others", "Comprehension of verbal communication.", "pi pi-comment")}
                            <div className="p-3">
                                <p className="font-semibold text-sm mb-2">Level</p>
                                <div className="mb-3">{radioLevel("commUnderstandLevel", COMM_UNDERSTAND_LEVELS)}</div>
                                <p className="font-semibold text-sm mb-2">Caregiver Will</p>
                                {cb("commUnderstandSpeakSlowly", "Speak slowly and clearly")}
                                {cb("commUnderstandRepeat", "Repeat as needed")}
                                {cb("commUnderstandEyeContact", "Use good eye contact")}
                                <div className="mt-2">
                                    <label className="font-medium text-sm block mb-1">Comments</label>
                                    <Controller name="commUnderstandComments" control={control} render={({ field: f }) => <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Comments" />} />
                                </div>
                            </div>
                        </Card>

                        {/* Dental */}
                        <Card style={appSectionCardStyle} className="mb-3">
                            {renderSurfaceHeader("Dental", "Dental status and oral care needs.", "pi pi-heart")}
                            <div className="p-3">
                                <div className="mb-3">
                                    <label className="font-medium text-sm block mb-1">Date of last exam</label>
                                    <Controller name="dentalDateOfExam" control={control} render={({ field: f }) => (
                                        <NurseDateField value={f.value} onChange={f.onChange} placeholder="Date of last dental exam" style={{ width: 220 }} />
                                    )} />
                                </div>
                                <div className="grid mb-2">
                                    <div className="col-6 md:col-4">{cb("dentalNoProblem", "No problem identified")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalOwnTeeth", "Own teeth")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalMissingTeeth", "Missing teeth")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalDenturesUpper", "Dentures — Upper")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalDenturesLower", "Dentures — Lower")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalPartialsUpper", "Partials — Upper")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalPartialsLower", "Partials — Lower")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalBrokenLoose", "Broken/loose teeth")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalInflamedGums", "Inflamed/bleeding gums")}</div>
                                    <div className="col-6 md:col-4">{cb("dentalDryMouth", "Dry mouth")}</div>
                                </div>
                                <p className="font-semibold text-sm mb-2">Caregiver Will</p>
                                {cb("dentalCaregiverInsertRemove", "Help with inserting/removing dentures")}
                                {cb("dentalCaregiverClean", "Help with cleaning dentures")}
                                <div className="mt-2">
                                    <label className="font-medium text-sm block mb-1">Comments</label>
                                    <Controller name="dentalComments" control={control} render={({ field: f }) => <InputTextarea {...f} className="w-full" rows={2} autoResize placeholder="Dental comments" />} />
                                </div>
                            </div>
                        </Card>

                    </>
                )}
                <div className="flex justify-content-center gap-2 mt-3">
                    <Button type="button" label="Prev" icon="pi pi-arrow-left" severity="secondary" outlined rounded onClick={onBack} />
                    <Button type="button" label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded visible={!enableEditing} onClick={() => setEnableEditing(true)} />
                    <Button type="button" label="Disable Edit" icon="pi pi-lock" severity="secondary" outlined rounded visible={enableEditing} onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
                    <Button type="submit" label="Save" icon="pi pi-save" severity="warning" outlined rounded loading={loading} visible={enableEditing} />
                    <Button type="button" label="Next" icon="pi pi-arrow-right" iconPos="right" severity="info" outlined rounded onClick={onNext} />
                </div>
            </form>
        </>
    );
};

export default Step09SensoryCommunication;
